import React, { memo, useMemo } from 'react';
import ReactFlow, { Handle, Position, useReactFlow } from 'reactflow';
import { useParams } from 'react-router-dom';
import CustomResizer from './CustomResizer';
import { Card, Typography, Slider, Row, Col, Space } from 'antd';
import { DeploymentUnitOutlined } from '@ant-design/icons';
import NodeLabel from './NodeLabel';
import { useNodeTitle } from './useNodeTitle';
import html2canvas from 'html2canvas';
import api from '../../../api/client';
import { PlayCircleFilled, LoadingOutlined } from '@ant-design/icons';
import { Button, message } from 'antd';
import { v4 as uuidv4 } from 'uuid';

const { Text, Title } = Typography;

// Core Drives Definition with configuration for the radar chart
const coreDrives = [
    { key: 'epicMeaning', label: 'Epic Meaning', angle: -90, color: '#fadb14' },      // Top
    { key: 'empowerment', label: 'Empowerment', angle: -45, color: '#fa8c16' },       // Top Right
    { key: 'socialInfluence', label: 'Social Infl.', angle: 0, color: '#fa541c' },    // Right
    { key: 'unpredictability', label: 'Unpredictability', angle: 45, color: '#f5222d' }, // Bottom Right
    { key: 'avoidance', label: 'Avoidance', angle: 90, color: '#eb2f96' },            // Bottom
    { key: 'scarcity', label: 'Scarcity', angle: 135, color: '#722ed1' },             // Bottom Left
    { key: 'ownership', label: 'Ownership', angle: 180, color: '#1890ff' },            // Left
    { key: 'accomplishment', label: 'Accomplishment', angle: 225, color: '#52c41a' },   // Top Left
];

const MAX_SCORE = 10;
const CHART_RADIUS = 120;
const CENTER = { x: 150, y: 150 }; // SVG Center (300x300 canvas)

const OctalysisNode = ({ id, data, selected }) => {
    const { setNodes } = useReactFlow();
    const { projectId: canvasId } = useParams();
    const onTitleChange = useNodeTitle(id, data);
    const nodeRef = React.useRef(null);
    const [capturing, setCapturing] = React.useState(false);

    // Initialize scores if not present
    const scores = data.scores || {};

    // Debounce Save Trigger
    const saveTimeoutRef = React.useRef(null);

    const updateScore = (key, value) => {
        // 1. Immediate Local Update (React Flow)
        setNodes((nds) => nds.map((node) => {
            if (node.id === id) {
                return {
                    ...node,
                    data: {
                        ...node.data,
                        scores: {
                            ...node.data.scores,
                            [key]: value
                        }
                    }
                };
            }
            return node;
        }));

        // 2. Deboune Persistence (Backend)
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }

        saveTimeoutRef.current = setTimeout(() => {
            if (data.onSave) {
                // We need the latest scores, but 'scores' here is from the render scope.
                // However, since we are inside the component, 'scores' might be stale in the closure if not careful.
                // Better approach: pass the COMPLETE new scores object to the timeout closure.
                // We need to construct the new scores object here first.
                const newScores = { ...data.scores, [key]: value };
                // wait, data.scores in the render scope might be stale if setNodes hasn't triggered a re-render yet?
                // Actually, setNodes triggers a re-render of this component? No, 'data' prop update triggers re-render.
                // 'data' prop comes from ReactFlow. 
                // To be safe, we should construct the new state here and pass it.
                data.onSave(id, { scores: newScores });
            }
        }, 500); // 500ms debounce
    };

    const handleCapture = async () => {
        if (!nodeRef.current) return;
        setCapturing(true);
        try {
            const canvas = await html2canvas(nodeRef.current, {
                backgroundColor: '#161b22', // Ensure background is captured
                scale: 2 // High resolution
            });

            canvas.toBlob(async (blob) => {
                if (!blob) {
                    message.error('Failed to capture node');
                    setCapturing(false);
                    return;
                }

                const formData = new FormData();
                formData.append('file', blob, `octalysis-${id}.png`);
                formData.append('bucket', 'media-generations'); // Use media-generations bucket

                try {
                    const response = await api.post('/storage/upload', formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                    });

                    if (response.data && response.data.url) {
                        message.success('Snapshot captured and saved!');
                        console.log('Snapshot saved:', response.data.url);

                        // Create Media Generation Entry
                        try {
                            await api.post('/media-generations', {
                                canvas_id: canvasId,
                                generation_id: uuidv4(),
                                node_id: id,
                                type: 'image',
                                url: response.data.url,
                                provider: 'octalysis',
                                model: 'snapshot',
                                prompt: 'Octalysis Diagram',
                                metadata: {
                                    width: 1200, // approximated from capture scale
                                    height: 1200,
                                    format: 'png'
                                }
                            });
                            message.success('Saved to Media Gallery');
                        } catch (dbError) {
                            console.error('Failed to save to DB:', dbError);
                            message.warning('Saved file but failed to record in gallery.');
                        }
                    }
                } catch (uploadError) {
                    console.error('Upload failed:', uploadError);
                    message.error('Failed to save snapshot');
                } finally {
                    setCapturing(false);
                }
            }, 'image/png');

        } catch (error) {
            console.error('Capture failed:', error);
            message.error('Failed to capture node');
            setCapturing(false);
        }
    };

    // Calculate Radar Polygon Points
    const polygonPoints = useMemo(() => {
        return coreDrives.map(drive => {
            const score = scores[drive.key] || 0;
            const normalizedScore = score / MAX_SCORE;
            // Radius scales with score
            const r = normalizedScore * CHART_RADIUS;
            const x = CENTER.x + r * Math.cos((drive.angle * Math.PI) / 180);
            const y = CENTER.y + r * Math.sin((drive.angle * Math.PI) / 180);
            return `${x},${y}`;
        }).join(' ');
    }, [scores]);

    // Calculate Background Axis Lines
    const axisLines = useMemo(() => {
        return coreDrives.map((drive, idx) => {
            const x = CENTER.x + CHART_RADIUS * Math.cos((drive.angle * Math.PI) / 180);
            const y = CENTER.y + CHART_RADIUS * Math.sin((drive.angle * Math.PI) / 180);
            return (
                <line
                    key={idx}
                    x1={CENTER.x}
                    y1={CENTER.y}
                    x2={x}
                    y2={y}
                    stroke="#30363d"
                    strokeWidth="1"
                    strokeDasharray="4 4"
                />
            );
        });
    }, []);

    // Outer Octagon helper
    const outerOctagon = useMemo(() => {
        return coreDrives.map(drive => {
            const x = CENTER.x + CHART_RADIUS * Math.cos((drive.angle * Math.PI) / 180);
            const y = CENTER.y + CHART_RADIUS * Math.sin((drive.angle * Math.PI) / 180);
            return `${x},${y}`;
        }).join(' ');
    }, []);

    return (
        <div ref={nodeRef} style={{ position: 'relative', height: '100%', width: '100%', padding: 20 }}>
            <CustomResizer
                isVisible={selected}
                minWidth={600}
                minHeight={600}
                onResizeEnd={(event, params) => {
                    const { width, height, x, y } = params;
                    if (data.onResize) {
                        data.onResize(id, { width, height, x, y });
                    }
                }}
            />
            <NodeLabel
                title={data.nodeTitle || 'Octalysis'}
                iconType="deployment-unit"
                color={data.nodeColor || '#faad14'}
                onTitleChange={onTitleChange}
            />

            <Card
                size="small"
                bodyStyle={{ padding: 24, height: '100%', display: 'flex', flexDirection: 'column', background: '#161b22', borderRadius: 8 }}
                style={{
                    width: '100%',
                    height: '100%',
                    border: '1px solid #30363d',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                    borderRadius: 8,
                    background: '#161b22'
                }}
            >
                <Row gutter={[24, 24]} style={{ flex: 1 }}>
                    {/* Left Column: Chart */}
                    <Col span={12} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                        {/* Labels for Quadrants */}
                        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
                            <Text style={{ position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', color: '#fff', opacity: 0.5, fontSize: 10 }}>WHITE HAT</Text>
                            <Text style={{ position: 'absolute', bottom: 10, left: '50%', transform: 'translateX(-50%)', color: '#fff', opacity: 0.5, fontSize: 10 }}>BLACK HAT</Text>
                            <Text style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: '#fff', opacity: 0.5, fontSize: 10, writingMode: 'vertical-rl' }}>INTRINSIC</Text>
                            <Text style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#fff', opacity: 0.5, fontSize: 10, writingMode: 'vertical-rl', transform: 'translateY(-50%) rotate(180deg)' }}>EXTRINSIC</Text>
                        </div>

                        <svg width="300" height="300" viewBox="0 0 300 300" style={{ overflow: 'visible' }}>
                            {/* Outer Shape */}
                            <polygon points={outerOctagon} fill="#0d1117" stroke="#30363d" strokeWidth="2" />

                            {/* Axis Lines */}
                            {axisLines}

                            {/* Data Polygon */}
                            <polygon points={polygonPoints} fill="rgba(24, 144, 255, 0.3)" stroke="#1890ff" strokeWidth="2" />

                            {/* Corner Labels (Drive Names aligned to vertices) */}
                            {coreDrives.map((drive, idx) => {
                                const r = CHART_RADIUS + 25; // Push label out
                                const x = CENTER.x + r * Math.cos((drive.angle * Math.PI) / 180);
                                const y = CENTER.y + r * Math.sin((drive.angle * Math.PI) / 180);
                                const anchor = drive.angle === -90 || drive.angle === 90 ? 'middle' :
                                    (drive.angle > -90 && drive.angle < 90) ? 'start' : 'end';

                                return (
                                    <text
                                        key={idx}
                                        x={x}
                                        y={y + 4} // slight vertical adjust
                                        fill={drive.color}
                                        fontSize="10"
                                        textAnchor={anchor}
                                        fontWeight="500"
                                    >
                                        {drive.label}
                                    </text>
                                );
                            })}
                        </svg>
                    </Col>

                    {/* Right Column: Sliders */}
                    <Col span={12} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 12 }}>
                        {coreDrives.map(drive => (
                            <div key={drive.key} className="nodrag" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Text style={{ color: drive.color || '#fff', fontSize: 12, fontWeight: 500 }}>{drive.label}</Text>
                                    <Text style={{ color: '#8b949e', fontSize: 12 }}>{scores[drive.key] || 0}</Text>
                                </div>
                                <Slider
                                    min={0}
                                    max={10}
                                    step={1}
                                    value={scores[drive.key] || 0}
                                    onChange={(val) => updateScore(drive.key, val)}
                                    trackStyle={{ backgroundColor: drive.color || '#1890ff' }}
                                    handleStyle={{ borderColor: drive.color || '#1890ff' }}
                                />
                            </div>
                        ))}
                    </Col>
                </Row>
            </Card>

            {/* Play/Capture Button */}
            <Button
                type="primary"
                shape="circle"
                icon={capturing ? <LoadingOutlined style={{ fontSize: 40 }} /> : <PlayCircleFilled style={{ fontSize: 40 }} />}
                onClick={handleCapture}
                className="nodrag"
                style={{
                    position: 'absolute',
                    right: -25,
                    bottom: -25,
                    width: 60, // Smaller than MediaNode
                    height: 60,
                    zIndex: 100,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                    border: '4px solid #161b22',
                    background: '#52c41a', // Green for success/go
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            />
        </div >
    );
};

export default memo(OctalysisNode);
